class SlideoutMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.viewModel = this.getViewModel(this);
    this.menuItems = this.querySelectorAll('.drawer-item-collapsible .navigation-drawer__button');
    this.slideSubmenus = this.querySelectorAll('.submenu.slide-in');
  }

  connectedCallback() {
    this.initMenu();
    this.initModalProxy();
    this.syncDesktopMenu();
    this.setupDesktopBreakpointListener();
  }

  disconnectedCallback() {
    if (this._desktopMq) {
      this._desktopMq.removeEventListener('change', this._onDesktopBreakpoint);
    }
  }

  setupDesktopBreakpointListener() {
    this._desktopMq = window.matchMedia('(min-width: 964px)');
    this._onDesktopBreakpoint = (e) => {
      if (e.matches) {
        this.closeAllSubmenus();
        this.viewModel.$modal?.close?.();
      }
    };
    this._desktopMq.addEventListener('change', this._onDesktopBreakpoint);
  }

  initMenu() {
    this.setupMenuInteractions();
    this.setupBackButtons();
  }

  initModalProxy() {
    const { $modal } = this.viewModel;
    if (!$modal) return;

    this.proxyModalMethod($modal, 'close', () => this.closeAllSubmenus());
    this.proxyModalMethod($modal, 'open', () => {
      this.disableAnimationTemporary();
    });
  }

  proxyModalMethod(modal, methodName, callback) {
    const originalMethod = modal[methodName].bind(modal);
    modal[methodName] = (...args) => {
      callback();
      return originalMethod(...args);
    };
  }

  setupMenuInteractions() {
    this.menuItems.forEach((menuItem) => {
      const submenu = this.getSubmenuFromToggle(menuItem);
      if (!submenu) return;

      const level = this.getMenuItemLevel(menuItem);
      menuItem.dataset.menuLevel = level;
      submenu.dataset.menuLevel = level;

      menuItem.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleSubmenu(submenu, menuItem);
      });
    });
  }

  getMenuItemLevel(element) {
    let level = 0;
    let parent = element.parentElement;
    while (parent && parent !== this) {
      if (parent.classList.contains('submenu')) level++;
      parent = parent.parentElement;
    }
    return level;
  }

  setupBackButtons() {
    this.querySelectorAll('.modal__title--back-button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const submenu = event.currentTarget.closest('.submenu');
        this.closeSubmenu(submenu);
      });
    });

    this.querySelectorAll('.close-btn').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeAllSubmenus();
      });
    });
  }

  getSubmenuFromToggle(toggleButton) {
    return toggleButton.closest('toggleable-icon-button')?.nextElementSibling;
  }

  getToggleFromSubmenu(submenu) {
    return submenu?.previousElementSibling?.querySelector('.navigation-drawer__button.js-toggle');
  }

  openSubmenu(submenu, toggle) {
    const level = submenu.dataset.menuLevel;
    if (level !== undefined) {
      this.querySelectorAll(`.submenu[data-menu-level="${level}"].open`).forEach((other) => {
        if (other !== submenu) this.closeSubmenu(other);
      });
      // close all menus and children menus
      this.querySelectorAll(`.submenu[data-menu-level="${Number(level) + 1}"].open`).forEach((other) => {
        if (other !== submenu) this.closeSubmenu(other);
      });
    }
    submenu.classList.add('open');
    toggle?.classList.add('is-active');
    toggle?.setAttribute('aria-expanded', 'true');
    this.toggleTopMenuScroll();
  }

  closeSubmenu(submenu) {
    const toggle = this.getToggleFromSubmenu(submenu);
    toggle?.classList.remove('is-active');
    toggle?.setAttribute('aria-expanded', 'false');

    if (submenu.matches('ul.collapsible')) {
      submenu.classList.remove('open');
      submenu.classList.add('collapsible-closing');
      const onEnd = () => {
        submenu.classList.remove('collapsible-closing', 'block-scroll');
        submenu.removeEventListener('animationend', onEnd);
      };
      submenu.addEventListener('animationend', onEnd);
    } else {
      submenu.classList.remove('open', 'block-scroll');
    }

    this.toggleTopMenuScroll();
  }

  toggleSubmenu(submenu, toggle) {
    const isOpen = submenu.classList.contains('open');
    isOpen ? this.closeSubmenu(submenu) : this.openSubmenu(submenu, toggle);
  }

  closeAllSubmenus() {
    this.slideSubmenus.forEach((submenu) => this.closeSubmenu(submenu));
  }

  toggleTopMenuScroll() {
    this.viewModel.$topLevelContent?.classList?.toggle('block-scroll');
  }

  disableAnimationTemporary() {
    this.viewModel.$navigation?.classList.add('no-animation');
    setTimeout(() => {
      this.viewModel.$navigation?.classList.remove('no-animation');
    }, 400);
  }

  syncDesktopMenu() {
    // The new <desktop-mega-menu> custom element handles desktop interactions.
    // Skip wiring the legacy desktop top-level menu to the mobile slideout on desktop.
    if (window.matchMedia('(min-width: 964px)').matches && document.querySelector('desktop-mega-menu')) return;

    document.querySelectorAll('.desktop-main-menu .desktop-menu__item-link .text-button__button').forEach((link) => {
      const identifier = link.getAttribute('data-id');
      const submenu = this.viewModel.$mobileDrawer?.querySelector(`#submenu-${identifier}`);
      if (!submenu) return;

      //Accessibilize!
      link.setAttribute('aria-haspopup', true);
      link.setAttribute('aria-expanded', false);

      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.disableAnimationTemporary();
        this.openSubmenu(submenu, this.getToggleFromSubmenu(submenu));
        this.viewModel.$modal?.open?.();
      });
    });
  }

  getViewModel(scope) {
    return {
      $modal: scope.closest('.mobile-nav-slideout'),
      $navigation: scope.querySelector('.slideout-menu-drawer__navigation'),
      $topLevelContent: scope.closest('.side-slideout__content'),
      $mobileDrawer: document.getElementById('mobile-nav-slideout'),
    };
  }
}

customElements.define('slideout-menu-drawer', SlideoutMenuDrawer);
